import React from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import styles from "./settings-layout.module.scss";
import { ColorPaletteIcon, ThreeDBox, KeyboardCableIcon } from "../../../../../assets/icons";
import { BellNotificationIcon } from "../../../../../assets/customermenu-icons";
import useWindowDimensions, { CurrentScreen } from "../../../../../hooks/use-window-dimensions";
import { EnergyProfileIcon, FaqIcon, ProfileDetailsIcon, SecurityIcon, TeamMgmIcon } from "../../../../../assets/customer-settings-icons";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../stores/store";
import { ApplicationRoutes } from "../../../../../routes";
import { CustomerType } from "../../../../../model/customer/customer-type";
import { useGetUserInfoQuery } from "../../../../../service/user-service";
import { useGetUnreadNotificationsCountQuery } from "../../../../../service/notification-service";
import { InSettingsContext } from "../../../customer-page-layout/CustomerPageLayout";
import { FeatureFlagName } from "../../../../../tenant/features/featureFlags";
import { useFeatureFlag } from "../../../../../tenant/features/useFeatureFlag";

const LegalIcon: React.FC = () => (
	<svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
		<path
			d="M12 3v18M5 21h14M12 6h7l-2.5 6a3.5 3.5 0 0 1-4.5 0L12 6Zm0 0H5l2.5 6a3.5 3.5 0 0 0 4.5 0"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
			fill="none"
		/>
	</svg>
);

interface SettingsMenuItem {
	id: string;
	label: string;
	icon: React.ReactNode;
	path: string;
	// Extra routes (subpages) that should keep this item highlighted as active.
	matchPaths?: string[];
	isNew?: boolean;
	// Omitted = always shown; false hides the item (e.g. feature off for this tenant).
	isShow?: boolean;
}

export const SettingsLayout: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const currentScreen: CurrentScreen = useWindowDimensions();
	const userInfo = useSelector((state: RootState) => state.user);
	const energyProfile = useSelector((state: RootState) => state.customerEnergyProfile);
	const { data: fullUserInfo } = useGetUserInfoQuery();
	const { data: unreadCount } = useGetUnreadNotificationsCountQuery();
	const isRemoteTokenLoadEnabled = useFeatureFlag(FeatureFlagName.REMOTE_TOKEN_LOAD);

	const accountNumber = energyProfile?.accountNumber || energyProfile?.meterNumber || "";
	const isCorporate = userInfo.customerType === CustomerType.Corporate;
	const organizationName = fullUserInfo?.customerDetails?.organizationName ?? "";
	const displayName = isCorporate && organizationName ? organizationName : `${userInfo.firstName} ${userInfo.lastName}`.trim();
	const avatarInitial = (displayName || userInfo.firstName || "").charAt(0).toUpperCase();

	const SETTINGS_BASE = `/${ApplicationRoutes.SETTINGS}`;

	const allMenuGroups: SettingsMenuItem[][] = [
		[
			{
				id: "profile-details",
				label: t("account.accountInformation"),
				icon: <ProfileDetailsIcon />,
				path: `${SETTINGS_BASE}/${ApplicationRoutes.PROFILE_DETAILS}`,
			},
			{
				id: ApplicationRoutes.TEAM_MANAGEMENT,
				label: t("account.sharedAccountManagement"),
				icon: <TeamMgmIcon />,
				path: `${SETTINGS_BASE}/${ApplicationRoutes.TEAM_MANAGEMENT}`,
			},
			{
				id: ApplicationRoutes.SECURITY,
				label: t("account.security"),
				icon: <SecurityIcon />,
				path: `${SETTINGS_BASE}/${ApplicationRoutes.SECURITY}`,
			},
		],
		[
			{
				id: "energy-profile",
				label: t("common.labels.energyProfile"),
				icon: <EnergyProfileIcon />,
				path: `${SETTINGS_BASE}/${ApplicationRoutes.CUSTOMER_ENERGY_PROFILE}`,
				matchPaths: [
					`${SETTINGS_BASE}/${ApplicationRoutes.CUSTOMER_ENERGY_PROFILE}/${ApplicationRoutes.CUSTOMER_MANAGE_METERS}`,
					`${SETTINGS_BASE}/${ApplicationRoutes.CUSTOMER_ENERGY_PROFILE}/${ApplicationRoutes.ADD_METER}`,
					`${SETTINGS_BASE}/${ApplicationRoutes.ENERGY_PROFILE_WELCOME}`,
					`${SETTINGS_BASE}/${ApplicationRoutes.ENERGY_PROFILE_CREATE}`,
					`${SETTINGS_BASE}/${ApplicationRoutes.ENERGY_PROFILE_NIN}`,
				],
			},
			{
				id: ApplicationRoutes.REMOTE_TOKEN,
				label: t("common.labels.remoteTokenLoad"),
				icon: <KeyboardCableIcon />,
				path: `${SETTINGS_BASE}/${ApplicationRoutes.REMOTE_TOKEN}`,
				isNew: true,
				isShow: isRemoteTokenLoadEnabled,
			},
			{
				id: ApplicationRoutes.CUSTOMER_APPEARANCE,
				label: t("account.appearance"),
				icon: <ColorPaletteIcon />,
				path: `${SETTINGS_BASE}/${ApplicationRoutes.CUSTOMER_APPEARANCE}`,
			},
		],
		[
			{
				id: ApplicationRoutes.CUSTOMER_FAQ,
				label: t("account.faqTitle"),
				icon: <FaqIcon />,
				path: `${SETTINGS_BASE}/${ApplicationRoutes.CUSTOMER_FAQ}`,
			},
			{
				id: ApplicationRoutes.CUSTOMER_LEGAL,
				label: t("account.legal"),
				icon: <LegalIcon />,
				path: `${SETTINGS_BASE}/${ApplicationRoutes.CUSTOMER_LEGAL}`,
			},
			{
				id: ApplicationRoutes.APP_INFORMATION,
				label: t("account.appInformation"),
				icon: <ThreeDBox />,
				path: `${SETTINGS_BASE}/${ApplicationRoutes.APP_INFORMATION}`,
			},
		],
	];

	const menuGroups = allMenuGroups.map(group => group.filter(item => item.isShow ?? true));

	const handleMenuClick = (path: string) => {
		navigate(path);
	};

	const isActive = (item: SettingsMenuItem) =>
		location.pathname === item.path || (item.matchPaths?.includes(location.pathname) ?? false);

	const isDetailPage = location.pathname !== "/settings";

	// Top-level settings pages have a sidebar entry, so their desktop back button is redundant.
	// Nested sub-pages (e.g. Energy Profile details) have no sidebar entry, so they keep it.
	const topLevelPaths = menuGroups.flat().map(item => item.path);
	const isTopLevelSettingsPage = location.pathname === "/settings" || topLevelPaths.includes(location.pathname);

	const accountInfoPath = `${SETTINGS_BASE}/${ApplicationRoutes.PROFILE_DETAILS}`;
	const groupTitles = [t("common.navigation.account"), t("account.web.preferences"), t("account.web.aboutAndSupport")];

	const profileAvatar = <div className={styles.avatar}>{userInfo?.image ? <img src={userInfo?.image} alt={t("account.web.profileImageAlt")} /> : avatarInitial}</div>;
	const profileName = <h2 className={styles.userName}>{displayName}</h2>;
	const profileAcct = accountNumber ? <p className={styles.accountNumber}>{accountNumber}</p> : null;
	const profileBadge = (
		<div className={styles.badgesRow}>
			{userInfo.customerType && userInfo.customerType !== CustomerType.Unknown && (
				<span className={`${styles.accountTypeBadge} ${isCorporate ? styles.badgeCorporate : styles.badgeIndividual}`}>{userInfo.customerType}</span>
			)}
		</div>
	);

	return (
		<InSettingsContext.Provider value={isTopLevelSettingsPage}>
		<div className={styles.settingsLayout}>
			<aside className={`${styles.sidebar} ${currentScreen.isMobile && isDetailPage ? styles.hiddenMobile : ""}`}>
				{/* Profile Header — tappable card on mobile (drills into Account Information) */}
				{currentScreen.isMobile ? (
					<button type="button" className={`${styles.profileHeader} ${styles.profileHeaderCard}`} onClick={() => navigate(accountInfoPath)} aria-label={t("account.accountInformation")}>
						<div className={styles.profileTopRow}>{profileAvatar}</div>
						{profileName}
						{profileAcct}
						{profileBadge}
						<span className={styles.profileChevron}>›</span>
					</button>
				) : (
					<div className={styles.profileHeader}>
						<div className={styles.profileTopRow}>
							{profileAvatar}
							<Link to={`/${ApplicationRoutes.CUSTOMER_NOTIFICATIONS}`} className={styles.bellButton} aria-label={t("common.labels.notifications")}>
								{!!unreadCount && unreadCount > 0 && <span className={styles.bellBadge}>{unreadCount > 99 ? "99+" : unreadCount}</span>}
								<BellNotificationIcon />
							</Link>
						</div>
						{profileName}
						{profileAcct}
						{profileBadge}
					</div>
				)}

				{/* Menu Groups */}
				<nav className={styles.menuNav}>
					{menuGroups.map((group, groupIndex) => (
						<React.Fragment key={groupIndex}>
							{currentScreen.isMobile && groupTitles[groupIndex] && <div className={styles.sectionLabel}>{groupTitles[groupIndex]}</div>}
							<div className={styles.menuGroup}>
								{group.map(item => (
									<button key={item.id} className={`${styles.menuItem} ${isActive(item) ? styles.active : ""}`} onClick={() => handleMenuClick(item.path)}>
										<span className={styles.menuIcon}>{item.icon}</span>
										<span className={styles.menuLabel}>{item.label}</span>
										{item.isNew && <span className={styles.newBadge}>{t("common.labels.newLabel")}</span>}
										<span className={styles.chevron}>›</span>
									</button>
								))}
							</div>
						</React.Fragment>
					))}
				</nav>
			</aside>

			{/* Main Content Area */}
			<main className={`${styles.mainContent} ${currentScreen.isMobile && !isDetailPage ? styles.hiddenMobile : ""}`}>
				<Outlet />
			</main>
		</div>
		</InSettingsContext.Provider>
	);
};

export default SettingsLayout;
